import {
  Stack,
  Text,
  Checkbox,
  CommandBar,
  DetailsList,
  IconButton,
  SelectionMode,
  PivotItem,
  Pivot,
} from '@fluentui/react';
import {
  Field,
  Input,
  Textarea,
  Dropdown,
  Option,
  RadioGroup,
  Radio,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { useState, useEffect } from 'react';
import { allowsImageAndVideo } from '@moko/utils';
import UserOrGroupSelector from '../../common/selectors/UsersOrGroupSelectors';
import { IFieldsTab, IField, IMetadata } from '../../services/metadataService';
import { useBoolean } from '@fluentui/react-hooks';
import verticalImage from '../../assets/Vertical.png';
import horizontalImage from '../../assets/Horizontal.png';
import horizontalStackedImage from '../../assets/Horizontal-Stacked.png';

export interface IFieldEditorV2Props {
  metadata?: IMetadata;
  setMetadata: (metadata: IMetadata) => void;
  setMetadataStr: (metadataStr: string) => void;
  entityType: string;
  references?: string[];
  currFieldIndex?: number;
  setCurrFieldIndex: (index?: number) => void;
  setSidePanel?: (
    sidePanel?:
      | 'LookupEditor'
      | 'FieldEditor'
      | 'ValidationEditor'
      | 'PermissionEditor'
  ) => void;
}

const FieldEditorV2 = (props: IFieldEditorV2Props) => {
  interface IAccessWithIndex {
    index?: number;
    id: number;
    name: string;
    isGroup: boolean;
    email?: string;
  }

  const DATE_SUPPORT_TIME_OPTION = 'supportTimeInDateField';
  const CONTACT_SHOW_EXTENSION_OPTION = 'showExtensionInContactField';
  const ALLOW_TEXT_SCAN_OPTION = 'allowTextScan';
  const FILE_URL_OPTION = 'fileUrl';
  const IMAGE_MEDIA_TYPE_OPTION = 'mediaType';

  const getPanelAddButtonLabelFromOptions = (
    options: IField['options']
  ): string | undefined => {
    if (options && typeof options === 'object') {
      const addButtonLabel = (options as { addButtonLabel?: unknown })
        .addButtonLabel;
      return typeof addButtonLabel === 'string' ? addButtonLabel : undefined;
    }

    return undefined;
  };

  const getPanelColumnsFromOptions = (
    options: IField['options']
  ): 1 | 2 | 3 => {
    if (options && typeof options === 'object' && !Array.isArray(options)) {
      const columns = (options as { columns?: unknown }).columns;
      if (columns === 2 || columns === 3) {
        return columns;
      }
    }

    return 1;
  };

  const buildPanelOptions = (
    columns: 1 | 2 | 3,
    addButtonLabel?: string
  ): IField['options'] => {
    const normalizedLabel = addButtonLabel?.trim();
    return {
      columns,
      ...(normalizedLabel ? { addButtonLabel: normalizedLabel } : {}),
    };
  };

  const getDateSupportTimeFromOptions = (options: IField['options']) => {
    if (!options || typeof options !== 'object' || Array.isArray(options)) {
      return false;
    }

    return (
      (options as { supportTimeInDateField?: unknown })[
        DATE_SUPPORT_TIME_OPTION
      ] === true
    );
  };

  const buildDateOptions = (
    supportsTime?: boolean
  ): IField['options'] | undefined =>
    supportsTime ? { [DATE_SUPPORT_TIME_OPTION]: true } : undefined;

  const getContactShowExtensionFromOptions = (options: IField['options']) => {
    if (!options || typeof options !== 'object' || Array.isArray(options)) {
      return false;
    }

    return (
      (options as { showExtensionInContactField?: unknown })[
        CONTACT_SHOW_EXTENSION_OPTION
      ] === true
    );
  };

  const buildContactOptions = (
    showExtension?: boolean
  ): IField['options'] | undefined =>
    showExtension ? { [CONTACT_SHOW_EXTENSION_OPTION]: true } : undefined;

  const getAllowTextScanFromOptions = (options: IField['options']) => {
    if (!options || typeof options !== 'object' || Array.isArray(options)) {
      return false;
    }

    return (
      (options as { allowTextScan?: unknown })[ALLOW_TEXT_SCAN_OPTION] === true
    );
  };

  const buildStringOptions = (
    allowTextScan?: boolean
  ): IField['options'] | undefined =>
    allowTextScan ? { [ALLOW_TEXT_SCAN_OPTION]: true } : undefined;

  const getFileUrlFromOptions = (options: IField['options']) => {
    if (!options || typeof options !== 'object' || Array.isArray(options)) {
      return undefined;
    }

    const fileUrl = (options as { fileUrl?: unknown })[FILE_URL_OPTION];
    return typeof fileUrl === 'string' ? fileUrl : undefined;
  };

  const buildFileViewerOptions = (
    fileUrl?: string
  ): IField['options'] | undefined =>
    fileUrl && fileUrl.trim()
      ? { [FILE_URL_OPTION]: fileUrl.trim() }
      : undefined;

  const getImageMediaTypeFromOptions = (
    options: IField['options']
  ): 'imageOnly' | 'imageAndVideo' =>
    allowsImageAndVideo(options) ? 'imageAndVideo' : 'imageOnly';

  const buildImageOptions = (
    mediaType: 'imageOnly' | 'imageAndVideo'
  ): IField['options'] | undefined =>
    mediaType === 'imageAndVideo'
      ? { [IMAGE_MEDIA_TYPE_OPTION]: mediaType }
      : undefined;

  // Field properties states
  const [fieldName, setFieldName] = useState<string>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.name || ''
      : ''
  );
  const [fieldLabel, setFieldLabel] = useState<string>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.label || ''
      : ''
  );
  const [fieldHint, setFieldHint] = useState<string | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.hint
      : undefined
  );
  const [selectedFieldType, setSelectedFieldType] = useState<
    string | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.fieldType
      : undefined
  );
  const [selectedReference, setSelectedReference] = useState<
    string | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.reference
      : undefined
  );
  const [allowMultipleSelection, setAllowMultipleSelection] = useState<
    boolean | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.multiSelect
      : undefined
  );
  const [fieldRadioButtonLayout, setFieldRadioButtonLayout] = useState<
    string | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.optionsLayout
      : undefined
  );
  const [fieldHidden, setFieldHidden] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.effect === 'Hide'
      : undefined
  );
  const [panelAllowAppend, setPanelAllowAppend] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.allowAppend
      : undefined
  );
  const [panelAddButtonLabel, setPanelAddButtonLabel] = useState<
    string | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? getPanelAddButtonLabelFromOptions(
          props.metadata?.fields[props.currFieldIndex]?.options
        )
      : undefined
  );
  const [panelColumns, setPanelColumns] = useState<1 | 2 | 3>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? getPanelColumnsFromOptions(
          props.metadata?.fields[props.currFieldIndex]?.options
        )
      : 1
  );

  const [selectedDependees, setSelectedDependees] = useState<string[]>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.dependees
          ?.split('|')
          .filter((d) => d) || []
      : []
  );
  const [selectedTab, setSelectedTab] = useState<string | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.tabName
      : undefined
  );
  const [userOrGroupOption, setUserOrGroupOption] = useState<
    string | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.options
      : undefined
  );
  const [supportTimeInDateField, setSupportTimeInDateField] = useState<
    boolean | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? getDateSupportTimeFromOptions(
          props.metadata?.fields[props.currFieldIndex]?.options
        )
      : undefined
  );
  const [showExtensionInContactField, setShowExtensionInContactField] =
    useState<boolean | undefined>(
      props.metadata?.fields && props.currFieldIndex !== undefined
        ? getContactShowExtensionFromOptions(
            props.metadata?.fields[props.currFieldIndex]?.options
          )
        : undefined
    );
  const [allowTextScan, setAllowTextScan] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? getAllowTextScanFromOptions(
          props.metadata?.fields[props.currFieldIndex]?.options
        )
      : undefined
  );
  const [fileUrl, setFileUrl] = useState<string | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? getFileUrlFromOptions(
          props.metadata?.fields[props.currFieldIndex]?.options
        )
      : undefined
  );
  const [imageMediaType, setImageMediaType] = useState<
    'imageOnly' | 'imageAndVideo'
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? getImageMediaTypeFromOptions(
          props.metadata?.fields[props.currFieldIndex]?.options
        )
      : 'imageOnly'
  );
  // Fields settings states
  const [fieldMandatory, setFieldMandatory] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.mandatory
      : undefined
  );
  const [fieldReadOnly, setFieldReadOnly] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.readonly
      : undefined
  );
  const [fieldReadOnlyForContractors, setFieldReadOnlyForContractors] =
    useState<boolean | undefined>(
      props.metadata?.fields && props.currFieldIndex !== undefined
        ? props.metadata?.fields[props.currFieldIndex]?.readonlyForContractors
        : undefined
    );
  const [fieldShowInGrid, setFieldShowInGrid] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.showInGrid
      : undefined
  );
  const [fieldFilterable, setFieldFilterable] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.filterable ?? true
      : undefined
  );
  const [showInDefaultFilters, setShowInDefaultFilters] = useState<
    boolean | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.showInDefaultFilters
      : undefined
  );
  const [fieldShowInClient, setFieldShowInClient] = useState<
    boolean | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.showInClient
      : undefined
  );
  const [fieldShowInReport, setFieldShowInReport] = useState<
    boolean | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.showInReport
      : undefined
  );
  const [fieldRepeatingMode, setFieldRepeatingMode] = useState<
    string | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.repeatingMode
      : undefined
  );
  const [fieldLabelAbove, setFieldLabelAbove] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.labelAbove
      : undefined
  );
  const [fieldFullWidth, setFieldFullWidth] = useState<boolean | undefined>(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.fullWidth
      : undefined
  );
  const [fieldCopyToOrderedEntity, setFieldCopyToOrderedEntity] = useState<
    boolean | undefined
  >(
    props.metadata?.fields && props.currFieldIndex !== undefined
      ? props.metadata?.fields[props.currFieldIndex]?.copyToOrderedEntity
      : undefined
  );

  const {
    metadata,
    setMetadata,
    setMetadataStr,
    references,
    currFieldIndex,
    setCurrFieldIndex,
  } = props;

  const [tempField, setTempField] = useState<IField | undefined>(
    currFieldIndex !== undefined && metadata?.fields
      ? metadata?.fields[currFieldIndex]
      : undefined
  );
  const [userOrGroupNames] = useState<Map<string, string>>();
  const [
    isUsersSelectorOpen,
    { setTrue: showUsersSelector, setFalse: hideUsersSelector },
  ] = useBoolean(false);

  const [fieldError, setFieldError] = useState<string>();
  const [fieldType, setFieldType] = useState(tempField?.fieldType);
  const [accesses, setAccesses] = useState<IAccessWithIndex[]>([]);
  const selectedRepeatingMode = fieldRepeatingMode?.startsWith('E')
    ? 'E'
    : fieldRepeatingMode;

  const isNestedPanel =
    selectedFieldType === 'Panel' &&
    !!(
      props.metadata?.fields &&
      props.currFieldIndex !== undefined &&
      props.metadata.fields[props.currFieldIndex]?.parentName
    );

  // Sync state with tempField changes
  useEffect(() => {
    if (tempField) {
      setFieldName(tempField.name || '');
      setFieldLabel(tempField.label || '');
      setFieldHint(tempField.hint);
      setSelectedFieldType(tempField.fieldType);
      setSelectedReference(tempField.reference);
      setAllowMultipleSelection(tempField.multiSelect);
      setSelectedDependees(
        tempField.dependees?.split('|').filter((d) => d) || []
      );
      setSelectedTab(tempField.tabName);
      setFieldRadioButtonLayout(tempField.optionsLayout);
      setFieldHidden(tempField.effect === 'Hide');
      setUserOrGroupOption(tempField.options);
      setSupportTimeInDateField(
        getDateSupportTimeFromOptions(tempField.options)
      );
      setShowExtensionInContactField(
        getContactShowExtensionFromOptions(tempField.options)
      );
      setAllowTextScan(getAllowTextScanFromOptions(tempField.options));
      setFileUrl(getFileUrlFromOptions(tempField.options));
      setImageMediaType(getImageMediaTypeFromOptions(tempField.options));
      setPanelAllowAppend(tempField.allowAppend);
      setPanelAddButtonLabel(
        getPanelAddButtonLabelFromOptions(tempField.options)
      );
      setPanelColumns(getPanelColumnsFromOptions(tempField.options));
      setFieldMandatory(tempField.mandatory);
      setFieldReadOnly(tempField.readonly);
      setFieldReadOnlyForContractors(tempField.readonlyForContractors);
      setFieldShowInGrid(tempField.showInGrid);
      setFieldFilterable(tempField.filterable ?? true);
      setShowInDefaultFilters(tempField.showInDefaultFilters);
      setFieldShowInClient(tempField.showInClient);
      setFieldShowInReport(tempField.showInReport);
      setFieldRepeatingMode(tempField.repeatingMode);
      setFieldLabelAbove(tempField.labelAbove);
      setFieldFullWidth(tempField.fullWidth);
      setFieldCopyToOrderedEntity(tempField.copyToOrderedEntity);
      setFieldType(tempField.fieldType);
    } else {
      // Reset all form fields when creating a new field
      setFieldName('');
      setFieldLabel('');
      setFieldHint(undefined);
      setSelectedFieldType(undefined);
      setSelectedReference(undefined);
      setAllowMultipleSelection(undefined);
      setSelectedDependees([]);
      setFieldRadioButtonLayout(undefined);
      setFieldHidden(undefined);
      setSelectedTab(undefined);
      setUserOrGroupOption(undefined);
      setSupportTimeInDateField(undefined);
      setShowExtensionInContactField(undefined);
      setAllowTextScan(undefined);
      setFileUrl(undefined);
      setImageMediaType('imageOnly');
      setPanelAllowAppend(undefined);
      setPanelAddButtonLabel(undefined);
      setPanelColumns(1);
      setFieldMandatory(undefined);
      setFieldReadOnly(undefined);
      setFieldReadOnlyForContractors(undefined);
      setFieldShowInGrid(undefined);
      setFieldFilterable(undefined);
      setShowInDefaultFilters(undefined);
      setFieldShowInClient(undefined);
      setFieldShowInReport(undefined);
      setFieldRepeatingMode(undefined);
      setFieldLabelAbove(undefined);
      setFieldFullWidth(undefined);
      setFieldCopyToOrderedEntity(undefined);
      setFieldType(undefined);
    }
  }, [tempField]);

  return (
    <FluentProvider theme={webLightTheme}>
      <Text variant="medium" style={{ color: 'red' }}>
        {fieldError}
      </Text>
      <Pivot>
        <PivotItem headerText="Field properties" itemKey="fieldProperties">
          <Stack style={{ width: '100%' }} tokens={{ childrenGap: 14 }}>
            <Field label="Name">
              <Input
                style={{ width: 360 }}
                value={fieldName}
                onChange={(_ev, data) => setFieldName(data.value)}
              />
            </Field>
            <Field label="Field type">
              <Dropdown
                style={{ width: 360 }}
                value={selectedFieldType ? selectedFieldType : undefined}
                onOptionSelect={(_ev, data) => {
                  const val = (data.optionValue ?? '').toString();
                  setFieldType(val);
                  setSelectedFieldType(val);
                }}
              >
                <Option value="String">String</Option>
                <Option value="Number">Number</Option>
                <Option value="NumberWithUnit">NumberWithUnit</Option>
                <Option value="Lookup">Lookup</Option>
                <Option value="Image">Image</Option>
                <Option value="FileViewer">FileViewer</Option>
                <Option value="AutoComplete">AutoComplete Lookup</Option>
                <Option value="UserSelector">User Selector</Option>
                <Option value="Date">Date</Option>
                <Option value="Coordinate">Coordinate</Option>
                <Option value="Address">Address</Option>
                <Option value="UserGroupSelector">User/Group selector</Option>
                <Option value="Panel">Panel</Option>
                <Option value="Checkbox">Checkbox</Option>
                <Option value="RadioButton">Radio Button</Option>
                <Option value="Text">Text</Option>
              </Dropdown>
            </Field>
            <Field label="Label">
              <Textarea
                style={{ width: 360 }}
                value={fieldLabel}
                rows={2}
                onChange={(_ev, data) => setFieldLabel(data.value)}
              />
            </Field>
            <Field label="Hint">
              <Input
                style={{ width: 360 }}
                value={fieldHint}
                onChange={(_ev, data) => setFieldHint(data.value)}
              />
            </Field>
            <Field label="Tab">
              <Dropdown
                style={{ width: 360 }}
                value={selectedTab ? selectedTab : undefined}
                onOptionSelect={(_ev, data) =>
                  setSelectedTab(data.optionValue?.toString())
                }
              >
                {(metadata?.tabs ?? []).map((tab: IFieldsTab) => (
                  <Option key={tab.tabName} value={tab.tabName}>
                    {tab.caption}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {fieldType &&
              [
                'AutoComplete',
                'Lookup',
                'NumberWithUnit',
                'RadioButton',
              ].includes(fieldType) && (
                <Field label="Reference">
                  <Dropdown
                    style={{ width: 360 }}
                    value={selectedReference ? selectedReference : undefined}
                    onOptionSelect={(_ev, data) =>
                      setSelectedReference(data.optionValue?.toString())
                    }
                  >
                    {(references ?? []).map((r) => (
                      <Option key={r} value={r}>
                        {r}
                      </Option>
                    ))}
                  </Dropdown>
                  {fieldType === 'Lookup' && (
                    <Checkbox
                      label="Allow multiple selection"
                      checked={allowMultipleSelection}
                      onChange={(_ev, checked) => {
                        setAllowMultipleSelection(checked || false);
                      }}
                      styles={{ root: { marginTop: 14 } }}
                    />
                  )}
                </Field>
              )}
            {fieldType === 'AutoComplete' && (
              <Field label="Dependees">
                <Dropdown
                  multiselect
                  style={{ width: 360 }}
                  selectedOptions={selectedDependees}
                  onOptionSelect={(_ev, data) => {
                    const selected = (data.selectedOptions ?? [])
                      .map((v) => v?.toString())
                      .filter(Boolean) as string[];
                    setSelectedDependees(selected);
                  }}
                >
                  {(metadata?.fields ?? []).map((f) => (
                    <Option key={f.name} value={f.name}>
                      {f.name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
            {fieldType === 'UserGroupSelector' && (
              <Field label="Pick the field should be for User or Group selection">
                <RadioGroup
                  value={userOrGroupOption}
                  onChange={(_ev, data) => setUserOrGroupOption(data.value)}
                  layout="horizontal"
                >
                  <Radio value="U" label="User" />
                  <Radio value="G" label="Group" />
                </RadioGroup>
              </Field>
            )}
            <br />
            <Checkbox
              label="Mandatory"
              checked={fieldMandatory}
              boxSide="end"
              onChange={(_ev, checked) => setFieldMandatory(checked || false)}
            />
            {fieldType !== 'Panel' &&
              fieldType !== 'Text' &&
              fieldType !== 'FileViewer' && (
                <>
                  <Checkbox
                    label="Read only"
                    checked={fieldReadOnly}
                    boxSide="end"
                    onChange={(_ev, checked) =>
                      setFieldReadOnly(checked || false)
                    }
                  />
                  <Checkbox
                    label="Read only for contractors"
                    checked={fieldReadOnly || fieldReadOnlyForContractors}
                    disabled={fieldReadOnly}
                    boxSide="end"
                    onChange={(_ev, checked) =>
                      setFieldReadOnlyForContractors(checked || false)
                    }
                  />
                </>
              )}
          </Stack>
        </PivotItem>
        <PivotItem headerText="Settings" itemKey="settings">
          <Stack style={{ width: '100%' }} tokens={{ childrenGap: 14 }}>
            {fieldType !== 'Panel' ? (
              <>
                {fieldType !== 'Text' &&
                  fieldType !== 'Signature' &&
                  fieldType !== 'File' && (
                    <Checkbox
                      label="Show in grid"
                      checked={fieldShowInGrid}
                      boxSide="end"
                      onChange={(_ev, checked) =>
                        setFieldShowInGrid(checked || false)
                      }
                    />
                  )}
                {(fieldType === 'String' ||
                  fieldType === 'Number' ||
                  fieldType === 'Lookup' ||
                  fieldType === 'Date' ||
                  fieldType === 'AutoComplete' ||
                  fieldType === 'UserSelector' ||
                  fieldType === 'UserGroupSelector' ||
                  fieldType === 'Checkbox' ||
                  fieldType === 'RadioButton') && (
                  <>
                    <Checkbox
                      label="Filterable"
                      checked={fieldFilterable}
                      boxSide="end"
                      onChange={(_ev, checked) =>
                        setFieldFilterable(checked || false)
                      }
                    />
                    <Checkbox
                      label="Show in default filters"
                      checked={showInDefaultFilters}
                      boxSide="end"
                      onChange={(_ev, checked) =>
                        setShowInDefaultFilters(checked || false)
                      }
                    />
                  </>
                )}
                <Checkbox
                  label="Show in client"
                  checked={fieldShowInClient}
                  boxSide="end"
                  onChange={(_ev, checked) =>
                    setFieldShowInClient(checked || false)
                  }
                />
                <Checkbox
                  label="Show in report"
                  checked={fieldShowInReport}
                  boxSide="end"
                  onChange={(_ev, checked) =>
                    setFieldShowInReport(checked || false)
                  }
                />
                {fieldType !== 'Text' && (
                  <Checkbox
                    label="Show label above the field"
                    checked={fieldLabelAbove}
                    boxSide="end"
                    onChange={(_ev, checked) =>
                      setFieldLabelAbove(checked || false)
                    }
                  />
                )}
                <Checkbox
                  label="Full width"
                  checked={fieldFullWidth === true}
                  boxSide="end"
                  onChange={(_ev, checked) =>
                    setFieldFullWidth(checked || false)
                  }
                />
                {fieldType === 'RadioButton' && (
                  <Stack>
                    <Field label="Radio buttons layout">
                      <RadioGroup
                        value={fieldRadioButtonLayout}
                        onChange={(_ev, data) =>
                          setFieldRadioButtonLayout(data.value)
                        }
                        layout="horizontal"
                      >
                        <Radio value="Vertical" label="Vertical" />
                        <Radio value="Horizontal" label="Horizontal" />
                        <Radio
                          value="HorizontalStacked"
                          label="Horizontal stacked"
                        />
                      </RadioGroup>
                    </Field>
                    {fieldRadioButtonLayout === 'Vertical' && (
                      <img
                        alt="vertical"
                        src={verticalImage}
                        style={{ marginTop: 35, marginLeft: 10, height: 72 }}
                      />
                    )}
                    {fieldRadioButtonLayout === 'Horizontal' && (
                      <img
                        alt="horizontal"
                        src={horizontalImage}
                        style={{ marginTop: 35, marginLeft: 10, height: 30 }}
                      />
                    )}
                    {fieldRadioButtonLayout === 'HorizontalStacked' && (
                      <img
                        alt="horizontal-stacked"
                        src={horizontalStackedImage}
                        style={{ marginTop: 35, marginLeft: 10, height: 45 }}
                      />
                    )}
                  </Stack>
                )}
                {fieldType === 'Date' && (
                  <Stack>
                    <Checkbox
                      label="Support time"
                      checked={supportTimeInDateField === true}
                      boxSide="end"
                      onChange={(_ev, checked) => {
                        setSupportTimeInDateField(checked || false);
                      }}
                    />
                  </Stack>
                )}
                {fieldType === 'Contact' && (
                  <Checkbox
                    label="Show extension"
                    checked={showExtensionInContactField === true}
                    boxSide="end"
                    onChange={(_ev, checked) => {
                      setShowExtensionInContactField(checked || false);
                    }}
                  />
                )}
                {fieldType === 'String' && (
                  <Checkbox
                    label="Allow text scanning on mobile"
                    checked={allowTextScan}
                    boxSide="end"
                    onChange={(_ev, checked) => {
                      setAllowTextScan(checked || false);
                    }}
                  />
                )}
                {fieldType === 'FileViewer' && (
                  <Field label="File URL">
                    <Input
                      style={{ width: 360 }}
                      value={fileUrl ?? ''}
                      placeholder="https://example.com/file.pdf"
                      onChange={(_ev, data) => setFileUrl(data.value)}
                    />
                  </Field>
                )}
                {fieldType === 'Image' && (
                  <Field label="Media support">
                    <Dropdown
                      style={{ width: 360 }}
                      value={
                        imageMediaType === 'imageAndVideo'
                          ? 'Image and video'
                          : 'Image only'
                      }
                      selectedOptions={[imageMediaType]}
                      onOptionSelect={(_ev, data) => {
                        const val = data.optionValue;
                        if (val === 'imageOnly' || val === 'imageAndVideo') {
                          setImageMediaType(val);
                        }
                      }}
                    >
                      <Option value="imageOnly">Image only</Option>
                      <Option value="imageAndVideo">Image and video</Option>
                    </Dropdown>
                  </Field>
                )}
                <Checkbox
                  label="Hidden"
                  checked={fieldHidden}
                  boxSide="end"
                  onChange={(_ev, checked) => setFieldHidden(checked || false)}
                />
                {props.entityType === 'Order' && (
                  <Checkbox
                    label="Copy to ordered entity"
                    checked={fieldCopyToOrderedEntity}
                    boxSide="end"
                    onChange={(_ev, checked) =>
                      setFieldCopyToOrderedEntity(checked || false)
                    }
                  />
                )}
              </>
            ) : (
              <>
                <Field label="Columns">
                  <Dropdown
                    value={panelColumns.toString()}
                    selectedOptions={[panelColumns.toString()]}
                    style={{ width: '100px', minWidth: 0 }}
                    onOptionSelect={(_ev, data) => {
                      const columns = Number(data.optionValue);
                      if (columns === 1 || columns === 2 || columns === 3) {
                        setPanelColumns(columns);
                      }
                    }}
                  >
                    <Option value="1">1</Option>
                    <Option value="2">2</Option>
                    <Option value="3">3</Option>
                  </Dropdown>
                </Field>
                <Field label="Repeatable">
                  <RadioGroup
                    value={selectedRepeatingMode}
                    onChange={(_ev, data) => setFieldRepeatingMode(data.value)}
                  >
                    <Radio value="N" label="No" />
                    <Radio value="M" label="Manual" />
                    <Radio
                      value="E"
                      label="Per Entity"
                      disabled={props.entityType !== 'Work' || isNestedPanel}
                    />
                    <Dropdown
                      disabled={
                        !fieldRepeatingMode?.startsWith('E') || isNestedPanel
                      }
                      value={fieldRepeatingMode?.replace('E:', '')}
                      onOptionSelect={(_ev, data) => {
                        setFieldRepeatingMode(data.optionValue);
                      }}
                    >
                      {props.entityType === 'Work' && (
                        <Option value="E:Asset">Asset</Option>
                      )}
                    </Dropdown>
                  </RadioGroup>
                </Field>
                {fieldRepeatingMode?.startsWith('E') && (
                  <>
                    <Checkbox
                      label="Allow appending new entries to the panel"
                      checked={panelAllowAppend}
                      boxSide="end"
                      onChange={(_ev, checked) =>
                        setPanelAllowAppend(checked || false)
                      }
                    />
                    {panelAllowAppend && (
                      <Field label="Label for add button">
                        <Input
                          style={{ width: 360 }}
                          value={panelAddButtonLabel ?? ''}
                          onChange={(_ev, data) =>
                            setPanelAddButtonLabel(data.value)
                          }
                        />
                      </Field>
                    )}
                  </>
                )}
                <Checkbox
                  label="Hidden"
                  checked={fieldHidden}
                  boxSide="end"
                  onChange={(_ev, checked) => setFieldHidden(checked || false)}
                />
                <Checkbox
                  label="Show in report"
                  checked={fieldShowInReport}
                  boxSide="end"
                  onChange={(_ev, checked) =>
                    setFieldShowInReport(checked || false)
                  }
                />
              </>
            )}
          </Stack>
        </PivotItem>
        <PivotItem headerText="Permissions" itemKey="permissions">
          {/* <Text variant="large">Permissions</Text> */}
          <CommandBar
            items={[
              {
                key: 'addPermission',
                text: 'Add',
                iconProps: { iconName: 'Add' },
                onClick: () => {
                  showUsersSelector();
                },
              },
            ]}
          />
          <DetailsList
            columns={[
              {
                key: 'name',
                fieldName: 'name',
                name: 'User|Group Name',
                minWidth: 120,
              },
              {
                key: 'id',
                fieldName: 'id',
                name: 'User|Group Id',
                minWidth: 110,
                onRender(item) {
                  return (
                    <Stack horizontal grow={true} tokens={{ childrenGap: 5 }}>
                      <Text variant="large">{item.id}</Text>
                      <Text variant="small">
                        {item.isGroup ? `(Group)` : `(User)`}
                      </Text>
                    </Stack>
                  );
                },
              },
              {
                key: 'action',
                name: 'Action',
                minWidth: 85,
                onRender: (item: IAccessWithIndex) => (
                  <Stack horizontal>
                    <IconButton
                      iconProps={{ iconName: 'Delete' }}
                      onClick={() => {
                        const newAccesses = accesses?.filter(
                          (a) => a.id !== item.id
                        );
                        setAccesses(newAccesses);
                      }}
                    />
                  </Stack>
                ),
              },
            ]}
            items={
              accesses?.map((a) => ({
                key: a.id,
                id: a.id,
                name: userOrGroupNames?.get(a.name),
                isGroup: a.isGroup,
              })) ?? []
            }
            selectionMode={SelectionMode.none}
          />
        </PivotItem>
      </Pivot>
      <Stack horizontal horizontalAlign="end">
        <IconButton
          iconProps={{ iconName: 'CheckMark' }}
          onClick={() => {
            if (!selectedFieldType) {
              setFieldError('Type should be selected');
              return;
            }
            setFieldError(undefined);
            if (
              [
                'Lookup',
                'AutoComplete',
                'NumberWithUnit',
                'RadioButton',
              ].includes(selectedFieldType) &&
              selectedReference === undefined
            ) {
              setFieldError(
                `Type selected as ${selectedFieldType}, but missing reference list`
              );
              return;
            }
            if (
              fieldType === 'Panel' &&
              fieldRepeatingMode?.startsWith('E') &&
              !!(
                props.metadata?.fields &&
                props.currFieldIndex !== undefined &&
                props.metadata.fields[props.currFieldIndex]?.parentName
              )
            ) {
              setFieldError(
                'A panel inside another panel cannot use Per Entity repeat mode'
              );
              return;
            }
            if (
              fieldType === 'Panel' &&
              fieldRepeatingMode?.startsWith('E') &&
              !fieldRepeatingMode.includes(':')
            ) {
              setFieldError(
                'Select an entity type when using Per Entity repeat mode'
              );
              return;
            }

            if (metadata && tempField) {
              const fields = metadata?.fields;
              const newOrEditedField: IField = {
                name: fieldName,
                label: fieldLabel,
                hint: fieldHint,
                tabName: selectedTab,
                fieldType: selectedFieldType ?? '',
                lookupList: selectedReference,
                parentName: tempField?.parentName,
                dependees: selectedDependees.length
                  ? selectedDependees.join('|')
                  : undefined,
                reference: selectedReference,
                multiSelect: allowMultipleSelection,
                options:
                  selectedFieldType === 'UserGroupSelector'
                    ? userOrGroupOption
                    : selectedFieldType === 'Date'
                    ? buildDateOptions(supportTimeInDateField)
                    : selectedFieldType === 'Contact'
                    ? buildContactOptions(showExtensionInContactField)
                    : selectedFieldType === 'String'
                    ? buildStringOptions(allowTextScan)
                    : selectedFieldType === 'Image'
                    ? buildImageOptions(imageMediaType)
                    : selectedFieldType === 'FileViewer'
                    ? buildFileViewerOptions(fileUrl)
                    : selectedFieldType === 'Panel'
                    ? buildPanelOptions(
                        panelColumns,
                        panelAllowAppend ? panelAddButtonLabel : undefined
                      )
                    : undefined,
                mandatory: fieldMandatory,
                readonly:
                  selectedFieldType !== 'Panel' &&
                  selectedFieldType !== 'Text' &&
                  selectedFieldType !== 'FileViewer'
                    ? fieldReadOnly
                    : undefined,
                readonlyForContractors:
                  selectedFieldType !== 'Panel' &&
                  selectedFieldType !== 'Text' &&
                  selectedFieldType !== 'FileViewer'
                    ? fieldReadOnlyForContractors
                    : undefined,
                repeatingMode: fieldRepeatingMode,
                showInGrid: fieldShowInGrid,
                filterable: fieldFilterable,
                showInDefaultFilters: showInDefaultFilters,
                showInClient: fieldShowInClient,
                showInReport: fieldShowInReport,
                allowAppend: panelAllowAppend,
                optionsLayout: fieldRadioButtonLayout,
                effect: fieldHidden ? 'Hide' : 'Show',
                labelAbove: fieldLabelAbove,
                fullWidth: fieldFullWidth,
                copyToOrderedEntity: fieldCopyToOrderedEntity,
                permissions: accesses?.length
                  ? accesses.map((a) => `${a.isGroup ? 'G' : 'U'}${a.id}`)
                  : undefined,
              };
              if (currFieldIndex !== undefined) {
                fields[currFieldIndex] = newOrEditedField;
              } else {
                fields.push(newOrEditedField);
              }
              const newMetadata: IMetadata = {
                lookups: metadata.lookups ?? [],
                fields: [...(metadata?.fields ?? [])],
                validations: [...(metadata?.validations ?? [])],
                tabs: [...(metadata.tabs ?? [])],
                interactions: [...(metadata?.interactions ?? [])],
                cardLayout: metadata?.cardLayout,
              };
              setMetadata(newMetadata);
              setMetadataStr(JSON.stringify(newMetadata, null, 2));
            }
            props.setSidePanel?.(undefined);
          }}
        />
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          onClick={() => {
            setCurrFieldIndex(undefined);
            setTempField(undefined);
            props.setSidePanel?.(undefined);
          }}
        />
      </Stack>
      <UserOrGroupSelector
        isOpen={isUsersSelectorOpen}
        hideUsersAndGroupsSelector={hideUsersSelector}
        onUserOrGroupSelected={(
          id: number,
          isGroup: boolean,
          username?: string
        ) => {
          const userOrGroupKey = `${isGroup ? 'G' : 'U'}${id}`;
          userOrGroupNames?.set(userOrGroupKey, username ?? '');
          if (accesses) {
            setAccesses([
              ...accesses,
              { id: id, name: userOrGroupKey, isGroup: isGroup, email: '' },
            ]);
          } else {
            setAccesses([
              { id: id, name: userOrGroupKey, isGroup: isGroup, email: '' },
            ]);
          }
        }}
      />
    </FluentProvider>
  );
};

export default FieldEditorV2;
